import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const show = useCallback((message, type = 'info', duration = 3000) => {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const toast = {
    success: (msg, dur)  => show(msg, 'success', dur),
    error:   (msg, dur)  => show(msg, 'error',   dur || 4000),
    info:    (msg, dur)  => show(msg, 'info',     dur),
    warning: (msg, dur)  => show(msg, 'warning',  dur),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const TYPE_STYLES = {
  success: { bg: '#059669', icon: '✓' },
  error:   { bg: '#DC2626', icon: '✕' },
  warning: { bg: '#D97706', icon: '⚠' },
  info:    { bg: '#7C6FF7', icon: 'ℹ' },
};

function ToastContainer({ toasts }) {
  if (toasts.length === 0) return null;
  return (
    <div style={{
      position: 'fixed',
      top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      alignItems: 'center',
      pointerEvents: 'none',
      width: '100%',
      maxWidth: 360,
      padding: '0 16px',
      boxSizing: 'border-box',
    }}>
      {toasts.map(t => {
        const { bg, icon } = TYPE_STYLES[t.type] || TYPE_STYLES.info;
        return (
          <div key={t.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: bg,
            color: '#fff',
            borderRadius: 14,
            padding: '11px 16px',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            animation: 'toastIn 0.22s ease-out both',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
            <span style={{ flex: 1 }}>{t.message}</span>
          </div>
        );
      })}
    </div>
  );
}
